import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { logError } from '@/lib/sentry';
import { parseTemplateFile } from '@/utils/templateParser';
import {
  downloadInventoryTemplate,
  extractInventoryRows,
  hasDuplicateMapping,
  suggestInventoryMapping,
  type InventoryExcelParseResult,
  type InventoryFieldMapping,
  type InventoryMappingField,
} from '@/utils/inventoryExcel';
import { requestInventoryHeaderMapping } from '@/supabase/inventory/service/inventoryService';
import { normalizeItemName } from '@/supabase/inventory/normalizeItemName';
import type {
  BulkUpsertItemPayload,
  BulkUpsertMode,
  BulkUpsertResult,
  InventoryOverviewItem,
} from '@/supabase/inventory/types/inventory';
import { FileSelectStep, MappingConfirmStep, ModeSelectStep, PreviewStep, ResultStep } from './ExcelImportSteps';

export interface ExcelImportModalHandle {
  open: () => void;
}

interface ExcelImportModalContainerProps {
  bulkUpsert: (items: BulkUpsertItemPayload[], mode: BulkUpsertMode) => Promise<BulkUpsertResult | null>;
  isSubmitting: boolean;
  // replace 모드 삭제 예고 개수 계산용(useInventory가 이미 들고 있는 활성 목록 재사용 — 별도 조회 없음).
  activeItems: InventoryOverviewItem[];
}

interface ParsedFileState {
  file: File;
  sheetIndex: number;
  headers: string[];
  sampleRows: string[][];
}

type ImportStep = 'file' | 'mapping' | 'preview' | 'mode' | 'result';
type AnalysisStatus = 'idle' | 'parsing' | 'mapping-ai';

const ALLOWED_EXTENSIONS = ['.xlsx', '.xlsm'];
const EMPTY_MAPPING: InventoryFieldMapping = { name: null, stock: null, price: null, threshold: null };

const STEP_TITLE: Record<ImportStep, string> = {
  file: '엑셀 파일 선택',
  mapping: '열 매핑 확인',
  preview: '미리보기',
  mode: '등록 방식 선택',
  result: '완료',
};

const STEP_DESCRIPTION: Record<ImportStep, string> = {
  file: '재고 목록 엑셀 파일을 업로드해 상품을 한 번에 등록/갱신합니다.',
  mapping: '각 항목이 어느 열에 해당하는지 확인하고, 필요하면 직접 골라 주세요.',
  preview: '실제로 반영될 내용을 실행 전에 확인합니다.',
  mode: '기존 상품을 유지할지, 파일에 없는 상품을 정리할지 선택합니다.',
  result: '엑셀 일괄등록이 완료되었습니다.',
};

// 자체 확장자 게이트(carry-note #1) — templateParser.validateTemplateFile은 .xlsm을 거부하므로
// 재사용하지 않는다(읽기 참조만 하라는 PR5 제약 #1과도 맞음).
function hasAllowedExtension(fileName: string): boolean {
  const dotIdx = fileName.lastIndexOf('.');
  if (dotIdx < 0) return false;
  return ALLOWED_EXTENSIONS.includes(fileName.slice(dotIdx).toLowerCase());
}

// 실행 차단 사유(carry-note #3) — 유효 행 0개 / 파일 내 중복 / 2000행 캡 초과 중 먼저 걸리는 것.
function getPreviewBlockReason(result: InventoryExcelParseResult): string | null {
  if (result.rows.length === 0) return '실행할 수 있는 유효한 행이 없습니다. 오류 행을 고쳐서 다시 업로드해 주세요.';
  if (result.duplicates.length > 0) return '파일 안에 같은 상품명이 중복되어 있습니다. 중복을 해결한 뒤 다시 업로드해 주세요.';
  if (result.capExceeded) return '한 번에 등록할 수 있는 행은 최대 2,000개입니다. 파일을 나눠서 업로드해 주세요.';
  return null;
}

// replace 모드만 추가로 차단하는 사유(봇 P1 Fix A) — 셀 오류(재고 오타 등)로 error row가 된
// 상품은 rows(=file-norm 집합)에서 빠지므로, replace를 그대로 실행하면 "파일에 없는 상품"으로
// 오판돼 소프트삭제된다. 셀러 입장에선 "파일에 분명 있는데?" — 멘탈 모델 위반. upsert는 오류
// 행이 스킵될 뿐 삭제 위험이 없어 그대로 허용한다.
function getReplaceBlockReason(result: InventoryExcelParseResult): string | null {
  if (result.errors.length > 0) {
    return '오류 행이 있는 파일로는 전체 교체를 실행할 수 없습니다. 오류를 고치거나 추가/갱신만 사용해 주세요.';
  }
  return null;
}

/**
 * 엑셀 일괄등록 5단계 마법사(파일 선택 → 매핑 확인 → 미리보기 → 모드 선택 → 실행/결과)를
 * Inventory 페이지에서 분리해 자체 관리한다. `open()`만 imperative handle로 노출 → 부모는 ref
 * 호출만 하므로 모달 오픈 시 부모가 재렌더되지 않는다(MovementModalContainer 등 기존 모달
 * 컨테이너 선례와 동일한 forwardRef+sessionRef 패턴).
 *
 * ⚠️ React.memo로 감싸지 말 것. isSubmitting은 제출 중 버튼 비활성화를 실시간으로 반영해야 하는
 *    부모 live prop이다 — 다른 모달 컨테이너 선례와 동일하게 "open-state를 내부에 격리한
 *    컨테이너는 항상 memo 금지"로 통일한다.
 */
export const ExcelImportModalContainer = forwardRef<ExcelImportModalHandle, ExcelImportModalContainerProps>(
  ({ bulkUpsert, isSubmitting, activeItems }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<ImportStep>('file');
    const [parsedFile, setParsedFile] = useState<ParsedFileState | null>(null);
    const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>('idle');
    const [fileError, setFileError] = useState<string | null>(null);
    const [mapping, setMapping] = useState<InventoryFieldMapping>(EMPTY_MAPPING);
    const [isExtracting, setIsExtracting] = useState(false);
    const [previewResult, setPreviewResult] = useState<InventoryExcelParseResult | null>(null);
    const [mode, setMode] = useState<BulkUpsertMode>('upsert');
    const [result, setResult] = useState<BulkUpsertResult | null>(null);
    // 진행 중이던 비동기(파일 파싱/AI 매핑/미리보기 추출/실행)가 끝나기 전에 모달이 닫혔다가 다시
    // 열리면, 그 응답이 새 세션의 상태를 건드리지 않도록 세대를 구분한다(기존 모달 컨테이너 선례).
    const sessionRef = useRef(0);

    const resetForm = useCallback(() => {
      setStep('file');
      setParsedFile(null);
      setAnalysisStatus('idle');
      setFileError(null);
      setMapping(EMPTY_MAPPING);
      setIsExtracting(false);
      setPreviewResult(null);
      setMode('upsert');
      setResult(null);
    }, []);

    const open = useCallback(() => {
      sessionRef.current += 1;
      resetForm();
      setIsOpen(true);
    }, [resetForm]);

    useImperativeHandle(ref, () => ({ open }), [open]);

    const handleOpenChange = useCallback(
      (next: boolean) => {
        if (!next) {
          sessionRef.current += 1;
          resetForm();
        }
        setIsOpen(next);
      },
      [resetForm],
    );

    const handleFileChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0] ?? null;
      e.target.value = ''; // 같은 파일을 다시 선택해도 change 이벤트가 재발화되도록
      if (!selected) return;

      setFileError(null);
      if (!hasAllowedExtension(selected.name)) {
        setFileError('.xlsx 또는 .xlsm 파일만 업로드할 수 있습니다.');
        return;
      }

      const session = ++sessionRef.current;
      setAnalysisStatus('parsing');
      try {
        const parsed = await parseTemplateFile(selected);
        if (session !== sessionRef.current) return;

        const sheetIndex = parsed.recommendedSheetIndex;
        const sheet = parsed.sheets[sheetIndex];
        if (!sheet || sheet.headers.length === 0) {
          setAnalysisStatus('idle');
          setFileError('파일에서 헤더를 찾을 수 없습니다. 첫 행에 컬럼명이 있는지 확인해 주세요.');
          return;
        }

        // ① 키워드 매핑(즉시, 결정적)
        const suggestion = suggestInventoryMapping(sheet.headers);
        let nextMapping = suggestion.mapping;
        // name 미해결 또는 어느 필드든 모호(복수 매치)면 ① 실패 → ②로(PR5 매핑 계약).
        const needsAi = suggestion.unresolved.includes('name') || suggestion.ambiguous.length > 0;

        if (needsAi) {
          setAnalysisStatus('mapping-ai');
          // 실패해도 절대 흐름을 막지 않는다 — requestInventoryHeaderMapping은 항상
          // {mapping:null}로 수렴하는 계약(repo 레벨에서 이미 throw하지 않음).
          const aiResult = await requestInventoryHeaderMapping(sheet.headers, sheet.sampleData);
          if (session !== sessionRef.current) return;
          if (aiResult.mapping) {
            // 서버가 재검증하지만 클라도 범위 밖 colIdx는 null로 클램프(T3 리뷰 M2) —
            // 범위 밖 값이 들어오면 controlled select가 빈 값으로 렌더돼 혼란만 준다.
            const clampColIdx = (v: number | null) =>
              v !== null && Number.isInteger(v) && v >= 0 && v < sheet.headers.length ? v : null;
            nextMapping = {
              name: clampColIdx(aiResult.mapping.name),
              stock: clampColIdx(aiResult.mapping.stock),
              price: clampColIdx(aiResult.mapping.price),
              threshold: clampColIdx(aiResult.mapping.threshold),
            };
          }
        }

        setParsedFile({ file: selected, sheetIndex, headers: sheet.headers, sampleRows: sheet.sampleData });
        setMapping(nextMapping);
        // 이전 파일의 미리보기 잔존 방어(T3 리뷰 M3) — 현재는 handleMappingNext가 항상
        // 덮어쓰지만, 그 불변식에 기대지 않고 여기서 명시적으로 비운다.
        setPreviewResult(null);
        setAnalysisStatus('idle');
        setStep('mapping');
      } catch (err) {
        if (session !== sessionRef.current) return;
        logError('ExcelImportModalContainer.parseFile', err);
        setAnalysisStatus('idle');
        setFileError('파일을 읽는 중 문제가 발생했습니다. 파일 형식을 확인해 주세요.');
      }
    }, []);

    const handleMappingFieldChange = useCallback((field: InventoryMappingField, colIdx: number | null) => {
      setMapping((prev) => ({ ...prev, [field]: colIdx }));
    }, []);

    // 같은 열을 여러 필드에 중복 선택했는지 파생 계산(codexloop R1 Fix 2) — mapping state를
    // mirror하지 않고 매 렌더 그 state에서 다시 계산한다. MappingConfirmStep도 동일한 mapping
    // prop으로 같은 판정을 독립적으로 재계산해 안내 문구를 보여준다(단일 진실 = hasDuplicateMapping).
    const mappingHasDuplicateColumns = useMemo(() => hasDuplicateMapping(mapping), [mapping]);

    const handleChangeFile = useCallback(() => {
      sessionRef.current += 1;
      setParsedFile(null);
      setMapping(EMPTY_MAPPING);
      setPreviewResult(null);
      setFileError(null);
      setAnalysisStatus('idle');
      // 추출 in-flight 중 파일 변경 시: 세대 bump로 그 추출의 finally가 스킵되므로 여기서
      // 직접 리셋해야 한다 — 안 하면 새 파일의 매핑 스텝에서 '다음'이 영구 비활성(T3 리뷰 I1).
      setIsExtracting(false);
      setStep('file');
    }, []);

    const handleMappingNext = useCallback(async () => {
      if (!parsedFile || mapping.name === null) return;
      const session = sessionRef.current;
      setIsExtracting(true);
      try {
        const extracted = await extractInventoryRows(parsedFile.file, parsedFile.sheetIndex, mapping);
        if (session !== sessionRef.current) return;
        setPreviewResult(extracted);
        // 매핑 수정 → 재추출 경로 커버(봇 P1 Fix A) — 이전 세션에서 mode='replace'를 골라둔 채
        // 매핑을 바꿔 다시 추출했더니 이번엔 오류 행이 생긴 경우, 실행 불가 모드가 선택된 채로
        // 모드 스텝에 진입하지 않도록 여기서 선제적으로 upsert로 되돌린다(functional updater —
        // mode를 dep에 추가하지 않아도 되는 안정 참조).
        if (extracted.errors.length > 0) {
          setMode((prevMode) => (prevMode === 'replace' ? 'upsert' : prevMode));
        }
        setStep('preview');
      } catch (err) {
        if (session !== sessionRef.current) return;
        logError('ExcelImportModalContainer.extractRows', err);
        setParsedFile(null);
        // 실패 경로에서도 이전 미리보기 잔존 제거(T3 리뷰 M3)
        setPreviewResult(null);
        setFileError('파일을 다시 읽는 중 문제가 발생했습니다. 처음부터 다시 시도해 주세요.');
        setStep('file');
      } finally {
        if (session === sessionRef.current) setIsExtracting(false);
      }
    }, [parsedFile, mapping]);

    const handleBackToMapping = useCallback(() => setStep('mapping'), []);
    const handleBackToPreview = useCallback(() => setStep('preview'), []);

    const previewBlockReason = previewResult ? getPreviewBlockReason(previewResult) : null;
    // replace 모드 전용 차단 사유(봇 P1 Fix A) — 오류 행이 있으면 replace만 막고 upsert는
    // 그대로 preview→mode 전환을 허용한다(previewBlockReason과 별도 축).
    const replaceBlockReason = previewResult ? getReplaceBlockReason(previewResult) : null;

    const handlePreviewNext = useCallback(() => {
      if (!previewResult || getPreviewBlockReason(previewResult)) return;
      setStep('mode');
    }, [previewResult]);

    // replace 삭제 예고 개수 — 서버가 최종 판정 기준(norm)과 동일하게 클라에서도 미리 계산해
    // 보여준다(별도 RPC 없이 Inventory 페이지가 이미 들고 있는 activeItems 재사용).
    const replaceDeletionCount = useMemo(() => {
      if (!previewResult) return 0;
      const fileNorms = new Set(previewResult.rows.map((row) => normalizeItemName(row.name)));
      return activeItems.filter((item) => !fileNorms.has(item.norm_name)).length;
    }, [previewResult, activeItems]);

    const handleExecute = useCallback(async () => {
      if (!previewResult || getPreviewBlockReason(previewResult)) return;
      // 실행 시점 이중 가드(봇 P1 Fix A) — 모드 스텝에서 토글을 disabled로 막아도, 매핑을
      // 되돌아가 재추출하는 등의 경로로 mode='replace'가 오류-있는 previewResult와 함께 여기
      // 도달할 가능성을 실행 직전에 한 번 더 차단한다(getPreviewBlockReason과 동일한 이중 가드
      // 패턴 — carry-note #3).
      if (mode === 'replace' && getReplaceBlockReason(previewResult)) return;
      const payload: BulkUpsertItemPayload[] = previewResult.rows.map((row) => ({
        name: row.name,
        price: row.price,
        stock: row.stock,
        lowStockThreshold: row.lowStockThreshold,
      }));
      const session = sessionRef.current;
      const outcome = await bulkUpsert(payload, mode);
      if (session !== sessionRef.current) return;
      if (outcome) {
        setResult(outcome);
        setStep('result');
      }
    }, [previewResult, mode, bulkUpsert]);

    const analyzingLabel =
      analysisStatus === 'mapping-ai'
        ? 'AI가 헤더를 분석하는 중...'
        : analysisStatus === 'parsing'
          ? '파일을 읽는 중...'
          : null;

    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{STEP_TITLE[step]}</DialogTitle>
            <DialogDescription className="break-keep">{STEP_DESCRIPTION[step]}</DialogDescription>
          </DialogHeader>

          {step === 'file' && (
            <FileSelectStep
              fileError={fileError}
              isAnalyzing={analysisStatus !== 'idle'}
              analyzingLabel={analyzingLabel}
              onFileChange={handleFileChange}
              onDownloadTemplate={downloadInventoryTemplate}
            />
          )}
          {step === 'mapping' && parsedFile && (
            <MappingConfirmStep
              headers={parsedFile.headers}
              sampleRows={parsedFile.sampleRows}
              mapping={mapping}
              onFieldChange={handleMappingFieldChange}
            />
          )}
          {step === 'preview' && previewResult && <PreviewStep result={previewResult} blockReason={previewBlockReason} />}
          {step === 'mode' && previewResult && (
            <ModeSelectStep
              mode={mode}
              onModeChange={setMode}
              validRowCount={previewResult.rows.length}
              replaceDeletionCount={replaceDeletionCount}
              replaceBlockReason={replaceBlockReason}
            />
          )}
          {step === 'result' && result && <ResultStep result={result} />}

          <DialogFooter>
            {step === 'file' && (
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                취소
              </Button>
            )}
            {step === 'mapping' && (
              <>
                <Button variant="outline" onClick={handleChangeFile}>
                  다른 파일 선택
                </Button>
                <Button
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  onClick={handleMappingNext}
                  disabled={mapping.name === null || isExtracting || mappingHasDuplicateColumns}
                >
                  {isExtracting ? '미리보기 생성 중...' : '다음'}
                </Button>
              </>
            )}
            {step === 'preview' && (
              <>
                <Button variant="outline" onClick={handleBackToMapping}>
                  이전
                </Button>
                <Button
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  onClick={handlePreviewNext}
                  disabled={!!previewBlockReason}
                >
                  다음
                </Button>
              </>
            )}
            {step === 'mode' && (
              <>
                <Button variant="outline" onClick={handleBackToPreview} disabled={isSubmitting}>
                  이전
                </Button>
                <Button
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  onClick={handleExecute}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '등록 중...' : '실행'}
                </Button>
              </>
            )}
            {step === 'result' && (
              <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => handleOpenChange(false)}>
                닫기
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

ExcelImportModalContainer.displayName = 'ExcelImportModalContainer';
